<template>
    <div data-testid="device-card" class="p-4 transition-shadow duration-200 bg-white border border-gray-300 rounded-lg shadow-md cursor-pointer hover:shadow-md" @click="navigateToDevice">
        <div class="space-y-3">
            <div class="flex items-center justify-between">
                <div class="flex items-center flex-1 min-w-0 gap-3">
                    <div :class="[isDeviceOnline(device) ? 'bg-green-500' : 'bg-red-500', 'w-4 h-4 rounded-full flex-shrink-0']" data-testid="device-card-online-indicator" aria-hidden="true" />
                    <h3 data-testid="device-card-serial-number" class="text-xl font-bold text-gray-900 truncate">{{ device.serialNumber }}</h3>
                </div>

                <div v-if="hasDeviceAdmin" class="flex-shrink-0" @click.stop>
                    <input data-testid="device-card-checkbox" type="checkbox" class="w-4 h-4 text-blue-500 rounded focus:ring-blue-500 focus:ring-2" :value="device.id" :checked="deviceSelection.includes(device.id)" @change="updateSelection" />
                </div>
            </div>

            <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-600">{{ $t("alarms.alarm") }}</span>
                <div class="flex items-center gap-2">
                    <div :id="'networkTooltipItem' + device.serialNumber" @mouseover="showTooltip('network', true)" @mouseleave="showTooltip('network', false)">
                        <DeviceMobileIcon :class="['h-7 w-7 rounded-full', ...stateColor(device, 'network'), 'p-1.5']" aria-hidden="true" />
                        <AlarmTooltip v-if="tooltipVisible.network" :targetId="'networkTooltipItem' + device.serialNumber" type="network" :alert="getHighestAlert('network', device)" :device="device" :fallback="!getAlert('network') ? getOnlineStatus(device) : ''" />
                    </div>

                    <div>
                        <div :id="'wifiTooltipTrigger' + device.serialNumber" @mouseover="showTooltip('wifi', true)" @mouseleave="showTooltip('wifi', false)">
                            <WifiIcon v-if="isConnectedWifi" :class="['h-7 w-7 rounded-full', ...stateColor(device, 'wifi'), 'p-1.5']" aria-hidden="true" />
                            <div v-else class='flex items-center justify-center w-7 h-7 p-1.5 text-gray-400 bg-gray-100 rounded-full'>
                                <WifiIcon class="w-4 h-4" />
                            </div>
                        </div>
                        <AlarmTooltip v-if="tooltipVisible.wifi" :targetId="'wifiTooltipTrigger' + device.serialNumber" type="wifi" :alert="getHighestAlert('wifi', device)" :device="device" :fallback="!getAlert('wifi') ? getWifiStatus() : ''" />
                    </div>

                    <div :id="'powerSupplyStatusTooltipTrigger' + device.serialNumber" @mouseover="showTooltip('power', true)" @mouseleave="showTooltip('power', false)">
                        <BatteryIcon :class="['h-7 w-7 rounded-full', ...stateColor(device, 'power'), 'p-1.5']" aria-hidden="true" />
                        <AlarmTooltip v-if="tooltipVisible.power" :targetId="'powerSupplyStatusTooltipTrigger' + device.serialNumber" type="power" :alert="getHighestAlert('power', device)" :device="device" :fallback="!getAlert('power') ? getPowerStatus() : ''" />
                    </div>

                    <div :id="'braceletStatusTooltipTrigger' + device.serialNumber" @mouseover="showTooltip('accessories', true)" @mouseleave="showTooltip('accessories', false)">
                        <BraceletIcon :class="['h-7 w-7 rounded-full', ...stateColor(device, 'accessories'), 'p-1.5']" aria-hidden="true" />
                        <AlarmTooltip v-if="tooltipVisible.accessories" :targetId="'braceletStatusTooltipTrigger' + device.serialNumber" type="accessories" :alert="getHighestAlert('accessories', device)" :device="device" :fallback="!getAlert('accessories') ? getBraceletStatus() : ''" />
                    </div>

                    <div>
                        <CalendarIcon :class="['h-7 w-7 rounded-full', ...dailyCheckInStatusColor(device), 'p-1.5']" aria-hidden="true" />
                    </div>
                </div>
            </div>
        </div>

        <div class="flex justify-center pt-3 mt-3 border-t border-gray-100">
            <button data-testid="device-card-expand-button" @click.stop="isExpanded = !isExpanded" class="flex items-center gap-1 px-4 py-2 text-sm text-gray-500 transition-colors duration-200 rounded-md hover:text-gray-700 hover:bg-gray-50">
                <svg :class="['w-4 h-4 transition-transform duration-200', isExpanded ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
        </div>

        <div v-if="isExpanded" class="pt-3 mt-3 ">
            <div class="space-y-3">
                <div class="flex items-center justify-between">
                    <span class="font-medium text-gray-500">{{ $t("device.header.tenant") }}</span>
                    <span class="ml-2 font-medium text-gray-900 truncate">{{ device.owner.name }}</span>
                </div>

                <div v-if="getDeviceControllerID(device)" class="flex items-center justify-between">
                    <span class="font-medium text-gray-500">{{ $t("device.header.id") }}</span>
                    <span class="ml-2 font-mono text-sm text-gray-900 truncate">{{ getDeviceControllerID(device) }}</span>
                </div>

                <div v-if="device.configV2.accessories.bracelets.map(bracelet => bracelet.serial_number).join(', ')" class="flex items-start justify-between">
                    <span class="flex-shrink-0 font-medium text-gray-500">{{ $t("device.header.braceletColumn") }}</span>
                    <span class="ml-2 font-mono text-sm text-right text-gray-900 break-words">{{device.configV2.accessories.bracelets.map(bracelet => bracelet.serial_number).join(', ')}}</span>
                </div>

                <div v-if="isSuperAdmin && hasCallee(device)" class="flex items-center justify-between">
                    <span class="font-medium text-gray-500">{{ $t("device.header.arcConfig") }}</span>
                    <span class="ml-2 text-gray-900 truncate">{{ getFirstCallee(device) }}</span>
                </div>

                <div class="flex items-center justify-between">
                    <span class="font-medium text-gray-500">{{ $t("device.header.version") }}</span>
                    <span class="ml-2 font-mono text-sm text-gray-900 truncate">{{ device.firmware ? device.firmware.version : '-' }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    dailyCheckInStatusColor,
    stateColor,
} from "@/lib/statusColors";
import { DeviceMobileIcon, CalendarIcon, WifiIcon } from "@heroicons/vue/outline";
import { BatteryIcon } from "@/components/icons";
import { reactive, computed, ref } from "vue";
import { isDeviceOnline, hasWifiConnection, hasAlert, getHighestDescription, getHighestAlert } from "@/lib/alarms";
import { useI18n } from "vue-i18n";
import { getOnlineStatus, wifiStatusText, powerSupplyStatusText, dailyCheckInStatusText, braceletPresenceStatusText } from "@/lib/statusTexts";
import { BraceletIcon } from "@/components/icons";
import AlarmTooltip from "@/components/tooltips/AlarmTooltip.vue";
import { useRouter } from "vue-router";

export default {
    methods: {
        isDeviceOnline,
        getOnlineStatus,
        hasWifiConnection,
        getHighestDescription,
        getHighestAlert
    },
    components: {
        DeviceMobileIcon,
        CalendarIcon,
        BatteryIcon,
        WifiIcon,
        BraceletIcon,
        AlarmTooltip
    },
    name: "DeviceCard",
    props: {
        device: { required: true },
        isSuperAdmin: { required: true },
        hasDeviceAdmin: { required: true },
        deviceSelection: { required: true },
    },
    emits: ['update:deviceSelection'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const router = useRouter();

        const isExpanded = ref(false);
        const isConnectedWifi = computed(() => hasWifiConnection(props.device))

        const tooltipVisible = reactive({
            network: false,
            wifi: false,
            power: false,
            accessories: false,
        });

        const navigateToDevice = () => {
            router.push({ name: 'device', params: { serialNumber: props.device.serialNumber } });
        };

        const showTooltip = (type, show) => {
            tooltipVisible[type] = show;
        };

        const getAlert = (type) => {
            return hasAlert(props.device, type);
        };

        const getDeviceControllerID = (device) => {
            return [... new Set(device.configV2.alarm_app.destinations.filter(destination => destination.scaip).map(destination => destination.scaip.controller_id))].join(", ")
        };

        const hasCallee = (device) => {
            let arc = device.configV2.alarm_app.destinations.filter(destination => destination.call);
            return arc.length > 0 && arc[0].call.callee !== "";
        };

        const getFirstCallee = (device) => {
            let arc = device.configV2.alarm_app.destinations.filter(destination => destination.call);
            return arc.length > 0 ? arc[0].call.callee : "";
        };

        const updateSelection = (event) => {
            const deviceId = props.device.id;
            const isChecked = event.target.checked;
            let newSelection = [...props.deviceSelection];

            if (isChecked) {
                if (!newSelection.includes(deviceId)) {
                    newSelection.push(deviceId);
                }
            } else {
                newSelection = newSelection.filter(id => id !== deviceId);
            }

            emit('update:deviceSelection', newSelection);
        };

        const getWifiStatus = () => {
            if (!isConnectedWifi.value) return t("alarms.wifiNotConnected")
            return wifiStatusText(props.device, t)
        };

        const getPowerStatus = () => {
            const status = powerSupplyStatusText(props.device, t)
            if (status == "-") return t("device.tooltip.powerSupplyStatus");
            return status;
        };

        const getBraceletStatus = () => {
            const status = braceletPresenceStatusText(props.device, t);
            if (status == "-") return t("device.tooltip.braceletPresenceStatus");
            return status;
        };

        const getDailyCheckInStatusText = () => {
            const status = dailyCheckInStatusText(props.device, t)
            if (status == "-") return t("device.tooltip.dailyCheckIn")
            return status;
        };

        return {
            isExpanded,
            isConnectedWifi,
            tooltipVisible,
            showTooltip,
            getAlert,
            getDeviceControllerID,
            hasCallee,
            getFirstCallee,
            updateSelection,
            getWifiStatus,
            getPowerStatus,
            getBraceletStatus,
            getDailyCheckInStatusText,
            stateColor,
            dailyCheckInStatusColor,
            navigateToDevice,
        };
    },
};
</script>

<style scoped>
.device-tab-active {
    box-shadow: 0 0 0 2px rgb(59 130 246 / 1);
}
</style>
